package com.arenaproplay.admin.data.me

import com.arenaproplay.admin.core.config.AppConfig
import com.arenaproplay.admin.core.logger.getLogger
import com.arenaproplay.admin.domain.me.*
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.serialization.DeserializationStrategy
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.serializer

/**
 * Talks to the user-scoped `/api/v1/...` endpoints (feed, chats). Distinct
 * from [com.arenaproplay.admin.data.admin.HttpAdminRepository] which targets
 * `/api/v1/admin/...` only.
 *
 * Responses follow the standard envelope `{ code, data, errors }` — this
 * helper unwraps `data` before deserializing.
 */
class HttpMeRepository(
    private val client: HttpClient,
) : MeRepository {

    private val logger = getLogger<HttpMeRepository>()
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    private suspend fun <T> getV1(
        path: String,
        params: Map<String, String> = emptyMap(),
        deserializer: DeserializationStrategy<T>,
    ): T {
        val response = client.get(AppConfig.getApiUrl("/v1$path")) {
            params.forEach { (k, v) -> parameter(k, v) }
        }
        return unwrap(response, deserializer, "GET $path")
    }

    private suspend fun <T> unwrap(
        response: HttpResponse,
        deserializer: DeserializationStrategy<T>,
        label: String,
    ): T {
        val text = response.bodyAsText()
        if (!response.status.isSuccess()) {
            logger.warn("v1 $label failed ${response.status}: ${text.take(200)}")
            throw IllegalStateException("$label -> ${response.status}")
        }
        val envelope = json.parseToJsonElement(text).jsonObject
        val data: JsonElement = envelope["data"] ?: error("no `data` in: ${text.take(200)}")
        return json.decodeFromJsonElement(deserializer, data)
    }

    override suspend fun getFeed(page: Int, size: Int): FeedPage =
        getV1(
            "/feed",
            mapOf("page" to page.toString(), "size" to size.toString()),
            serializer(),
        )

    override suspend fun getMyChats(page: Int, size: Int, search: String): ChatListPage {
        val params = buildMap {
            put("page", page.toString())
            put("size", size.toString())
            if (search.isNotBlank()) put("search", search)
        }
        return getV1("/chats", params, serializer())
    }
}
